import { useId, useState, type FormEvent } from 'react';
import { KeyRound, MoreHorizontal, Pencil, Power, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { PASSWORD_MIN_LENGTH, ROLE_KEYS, type AdminUser, type AdminUserStatus, type Role, type RoleKey } from '@quds/shared';
import { useAuth } from '@/auth/AuthContext';
import {
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DropdownMenu,
  ErrorState,
  FormField,
  Input,
  Modal,
  Select,
  SkeletonRows,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  fieldDescribedBy,
  useToast,
  type DropdownItem,
} from '@/components/ui';
import { createAdminUser, listAdminUsers, listRoles, resetAdminPassword, updateAdminUser, useQuery } from '@/data';
import { RecordStatusBadge } from '@/features/pharmacies/components/StatusBadges';
import { hasErrors, mapFieldIssues, type FieldErrors } from '@/features/pharmacies/validation';
import { ApiError, errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatDate, formatDateTime, formatRelative } from '@/lib/format';

type Dialog =
  | { kind: 'add' }
  | { kind: 'edit'; user: AdminUser }
  | { kind: 'role'; user: AdminUser }
  | { kind: 'status'; user: AdminUser }
  | { kind: 'reset'; user: AdminUser };

const PASSWORD_HINT = `Minimum ${PASSWORD_MIN_LENGTH} characters`;

/** Admin user management (Super Admin). The API enforces every rule shown here. */
export function AdminUsersCard() {
  const { user, can } = useAuth();
  const users = useQuery(listAdminUsers, []);
  const roles = useQuery(listRoles, []);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const canUpdate = can('admin.update');

  const rowActions = (u: AdminUser): DropdownItem[] => {
    const self = u.id === user?.id;
    return [
      { id: 'edit', label: 'Edit User', icon: <Pencil size={16} />, onSelect: () => setDialog({ kind: 'edit', user: u }) },
      {
        id: 'role',
        label: 'Change Role',
        icon: <ShieldCheck size={16} />,
        disabled: self,
        title: self ? 'You cannot change your own role' : undefined,
        onSelect: () => setDialog({ kind: 'role', user: u }),
      },
      {
        id: 'status',
        label: u.status === 'active' ? 'Deactivate' : 'Activate',
        icon: <Power size={16} />,
        tone: u.status === 'active' ? 'danger' : 'default',
        disabled: self,
        title: self ? 'You cannot deactivate your own account' : undefined,
        onSelect: () => setDialog({ kind: 'status', user: u }),
      },
      {
        id: 'reset',
        label: 'Reset Password',
        icon: <KeyRound size={16} />,
        separatorBefore: true,
        disabled: self,
        title: self ? 'Change your own password in My Account' : undefined,
        onSelect: () => setDialog({ kind: 'reset', user: u }),
      },
    ];
  };

  return (
    <Card className="xl:col-span-2">
      <CardHeader
        title="Admin Users"
        icon={<Users size={16} aria-hidden="true" />}
        description="Quds Technology staff who can sign in to Central Admin and what their role allows"
        actions={
          can('admin.create') && (
            <Button size="sm" leftIcon={<UserPlus size={14} aria-hidden="true" />} onClick={() => setDialog({ kind: 'add' })} disabled={!roles.data}>
              Add User
            </Button>
          )
        }
      />
      {users.error ? (
        <ErrorState message={users.error.message} action={<Button variant="secondary" onClick={users.reload}>Try again</Button>} />
      ) : (
        <TableContainer label="Admin users table">
          <Table className="min-w-[880px]" aria-busy={users.loading || users.refreshing}>
            <THead>
              <tr>
                <TH className="pl-5">Name</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Last Login</TH>
                <TH>Created</TH>
                <TH align="right" className="pr-5">
                  Actions
                </TH>
              </tr>
            </THead>
            <TBody>
              {users.loading ? (
                <SkeletonRows rows={3} columns={7} />
              ) : (
                users.data?.items.map((u) => (
                  <TR key={u.id}>
                    <TD className="pl-5 font-medium text-ink">
                      {u.displayName}
                      {u.id === user?.id && <span className="ml-1.5 text-xs font-normal text-ink-muted">(you)</span>}
                    </TD>
                    <TD className="text-ink-secondary">{u.email}</TD>
                    <TD>{u.role.name}</TD>
                    <TD>
                      <RecordStatusBadge status={u.status} />
                      {u.mustChangePassword && <span className="mt-1 block text-xs text-ink-muted">Password change pending</span>}
                    </TD>
                    <TD className="text-ink-secondary">
                      {u.lastLoginAt ? <span title={formatDateTime(u.lastLoginAt)}>{formatRelative(u.lastLoginAt)}</span> : <span className="text-ink-muted">Never</span>}
                    </TD>
                    <TD className="text-ink-secondary">
                      <span title={formatDateTime(u.createdAt)}>{formatDate(u.createdAt)}</span>
                    </TD>
                    <TD align="right" className="pr-5">
                      {canUpdate && roles.data && (
                        <DropdownMenu
                          items={rowActions(u)}
                          label={`Actions for ${u.displayName}`}
                          renderTrigger={(props) => (
                            <button
                              {...props}
                              type="button"
                              aria-label={`Actions for ${u.displayName}`}
                              className={cn(
                                'ml-auto flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:shadow-focus focus-visible:outline-none',
                                props['aria-expanded'] && 'bg-surface-sunken text-ink',
                              )}
                            >
                              <MoreHorizontal size={18} aria-hidden="true" />
                            </button>
                          )}
                        />
                      )}
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </TableContainer>
      )}
      {roles.data && dialog?.kind === 'add' && <AdminUserFormModal user={null} roles={roles.data} isSelf={false} onClose={() => setDialog(null)} />}
      {roles.data && dialog?.kind === 'edit' && (
        <AdminUserFormModal user={dialog.user} roles={roles.data} isSelf={dialog.user.id === user?.id} onClose={() => setDialog(null)} />
      )}
      {roles.data && dialog?.kind === 'role' && <ChangeRoleModal user={dialog.user} roles={roles.data} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'status' && <StatusDialog user={dialog.user} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'reset' && <ResetPasswordModal user={dialog.user} onClose={() => setDialog(null)} />}
    </Card>
  );
}

function SubmitError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="mb-4 rounded-md border border-danger/20 bg-danger-soft px-3 py-2 text-[13px] text-danger">
      {message}
    </p>
  );
}

const roleOptions = (roles: Role[]) => ROLE_KEYS.map((k) => ({ value: k, label: roles.find((r) => r.key === k)?.name ?? k }));

interface FormValues {
  email: string;
  displayName: string;
  roleKey: RoleKey;
  status: AdminUserStatus;
  password: string;
  confirm: string;
}

function AdminUserFormModal({ user, roles, isSelf, onClose }: { user: AdminUser | null; roles: Role[]; isSelf: boolean; onClose: () => void }) {
  const { notify } = useToast();
  const { refreshUser } = useAuth();
  const base = useId();
  const id = (f: keyof FormValues) => `${base}-${f}`;
  const [values, setValues] = useState<FormValues>({
    email: user?.email ?? '',
    displayName: user?.displayName ?? '',
    roleKey: user?.role.key ?? 'SUPPORT',
    status: user?.status ?? 'active',
    password: '',
    confirm: '',
  });
  const [errors, setErrors] = useState<FieldErrors<FormValues>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const role = roles.find((r) => r.key === values.roleKey);

  const validate = (v: FormValues): FieldErrors<FormValues> => {
    const e: FieldErrors<FormValues> = {};
    if (v.displayName.trim().length < 2) e.displayName = 'Enter a full name (at least 2 characters).';
    if (!user) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = 'Enter a valid email address.';
      if (v.password.length < PASSWORD_MIN_LENGTH) e.password = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
      else if (v.password !== v.confirm) e.confirm = 'Passwords do not match.';
    }
    return e;
  };

  const patch = (p: Partial<FormValues>) => {
    const next = { ...values, ...p };
    setValues(next);
    if (hasErrors(errors)) setErrors(validate(next));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    setSubmitError(null);
    try {
      if (user) {
        await updateAdminUser(user.id, {
          displayName: values.displayName.trim(),
          ...(isSelf ? {} : { roleKey: values.roleKey, status: values.status }),
        });
        if (isSelf) await refreshUser();
        notify({ tone: 'success', title: 'User updated' });
      } else {
        await createAdminUser({
          email: values.email.trim(),
          displayName: values.displayName.trim(),
          password: values.password,
          roleKey: values.roleKey,
          status: values.status,
        });
        notify({
          tone: 'success',
          title: 'User created',
          description: 'Share the initial password through a secure channel. They will choose their own password at first sign-in.',
        });
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.fieldIssues.length) setErrors(mapFieldIssues<FormValues>(err.fieldIssues, {}));
      setSubmitError(errorMessage(err, 'Could not save the user.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={user ? 'Edit User' : 'Add User'}
      description={user?.email}
      icon={<Users size={18} aria-hidden="true" />}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="admin-user-form" loading={saving}>
            {user ? 'Save Changes' : 'Create User'}
          </Button>
        </>
      }
    >
      <SubmitError message={submitError} />
      <form id="admin-user-form" noValidate onSubmit={onSubmit} className="grid gap-4" autoComplete="off">
        <FormField label="Full Name" htmlFor={id('displayName')} required error={errors.displayName}>
          <Input id={id('displayName')} value={values.displayName} onChange={(e) => patch({ displayName: e.target.value })} maxLength={120} invalid={!!errors.displayName} aria-describedby={fieldDescribedBy(id('displayName'), { error: errors.displayName })} autoFocus />
        </FormField>
        {!user && (
          <FormField label="Email" htmlFor={id('email')} required error={errors.email} hint="Used to sign in. It cannot be changed later.">
            <Input id={id('email')} type="email" value={values.email} onChange={(e) => patch({ email: e.target.value })} invalid={!!errors.email} aria-describedby={fieldDescribedBy(id('email'), { hint: true, error: errors.email })} />
          </FormField>
        )}
        <FormField label="Role" htmlFor={id('roleKey')} hint={isSelf ? 'You cannot change your own role.' : role?.description}>
          <Select
            id={id('roleKey')}
            value={values.roleKey}
            onChange={(e) => patch({ roleKey: e.target.value as RoleKey })}
            options={roleOptions(roles)}
            disabled={isSelf}
            aria-describedby={fieldDescribedBy(id('roleKey'), { hint: true })}
          />
        </FormField>
        {!user && (
          <>
            <FormField
              label="Initial Password"
              htmlFor={id('password')}
              required
              error={errors.password}
              hint={`${PASSWORD_HINT}. It is not shown again, and the user must replace it at first sign-in.`}
            >
              <Input id={id('password')} type="password" autoComplete="new-password" value={values.password} onChange={(e) => patch({ password: e.target.value })} invalid={!!errors.password} aria-describedby={fieldDescribedBy(id('password'), { hint: true, error: errors.password })} />
            </FormField>
            <FormField label="Confirm Password" htmlFor={id('confirm')} required error={errors.confirm}>
              <Input id={id('confirm')} type="password" autoComplete="new-password" value={values.confirm} onChange={(e) => patch({ confirm: e.target.value })} invalid={!!errors.confirm} aria-describedby={fieldDescribedBy(id('confirm'), { error: errors.confirm })} />
            </FormField>
          </>
        )}
        <FormField
          label="Status"
          htmlFor={id('status')}
          hint={isSelf ? 'You cannot deactivate yourself.' : user ? 'Deactivating signs the user out everywhere.' : 'Inactive users cannot sign in until activated.'}
        >
          <Select
            id={id('status')}
            value={values.status}
            onChange={(e) => patch({ status: e.target.value as AdminUserStatus })}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            disabled={isSelf}
            aria-describedby={fieldDescribedBy(id('status'), { hint: true })}
          />
        </FormField>
      </form>
    </Modal>
  );
}

function ChangeRoleModal({ user, roles, onClose }: { user: AdminUser; roles: Role[]; onClose: () => void }) {
  const { notify } = useToast();
  const selectId = useId();
  const [roleKey, setRoleKey] = useState<RoleKey>(user.role.key);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const role = roles.find((r) => r.key === roleKey);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (roleKey === user.role.key) return onClose();
    setSaving(true);
    setSubmitError(null);
    try {
      await updateAdminUser(user.id, { roleKey });
      notify({ tone: 'success', title: 'Role changed', description: `${user.displayName} is now ${role?.name ?? roleKey}. It applies on their next action.` });
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err, 'Could not change the role.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Change Role"
      description={`${user.displayName} · ${user.email}`}
      icon={<ShieldCheck size={18} aria-hidden="true" />}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="change-role-form" loading={saving} disabled={roleKey === user.role.key}>
            Change Role
          </Button>
        </>
      }
    >
      <SubmitError message={submitError} />
      <form id="change-role-form" noValidate onSubmit={onSubmit} className="grid gap-4">
        <FormField label="Role" htmlFor={selectId} hint={role?.description}>
          <Select id={selectId} value={roleKey} onChange={(e) => setRoleKey(e.target.value as RoleKey)} options={roleOptions(roles)} aria-describedby={fieldDescribedBy(selectId, { hint: true })} autoFocus />
        </FormField>
      </form>
    </Modal>
  );
}

function StatusDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const { notify } = useToast();
  const [saving, setSaving] = useState(false);
  const deactivate = user.status === 'active';

  const onConfirm = async () => {
    setSaving(true);
    try {
      await updateAdminUser(user.id, { status: deactivate ? 'inactive' : 'active' });
      notify({ tone: 'success', title: deactivate ? 'User deactivated' : 'User activated' });
      onClose();
    } catch (err) {
      notify({ tone: 'error', title: deactivate ? 'Could not deactivate the user' : 'Could not activate the user', description: errorMessage(err, 'Please try again.') });
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open
      title={deactivate ? 'Deactivate User' : 'Activate User'}
      tone={deactivate ? 'danger' : 'primary'}
      confirmLabel={deactivate ? 'Deactivate' : 'Activate'}
      loading={saving}
      onCancel={onClose}
      onConfirm={() => void onConfirm()}
      message={
        deactivate ? (
          <>
            <span className="font-medium text-ink">{user.displayName}</span> will be signed out everywhere immediately and cannot sign in until
            reactivated.
          </>
        ) : (
          <>
            <span className="font-medium text-ink">{user.displayName}</span> will be able to sign in again with their current password.
          </>
        )
      }
    />
  );
}

interface ResetValues {
  newPassword: string;
  confirm: string;
}

function ResetPasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const { notify } = useToast();
  const base = useId();
  const id = (f: keyof ResetValues) => `${base}-${f}`;
  const [values, setValues] = useState<ResetValues>({ newPassword: '', confirm: '' });
  const [errors, setErrors] = useState<FieldErrors<ResetValues>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (v: ResetValues): FieldErrors<ResetValues> => {
    const e: FieldErrors<ResetValues> = {};
    if (v.newPassword.length < PASSWORD_MIN_LENGTH) e.newPassword = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
    else if (v.newPassword !== v.confirm) e.confirm = 'Passwords do not match.';
    return e;
  };

  const patch = (p: Partial<ResetValues>) => {
    const next = { ...values, ...p };
    setValues(next);
    if (hasErrors(errors)) setErrors(validate(next));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const { sessionsRevoked } = await resetAdminPassword(user.id, { newPassword: values.newPassword });
      notify({
        tone: 'success',
        title: 'Password reset',
        description: `${user.displayName} must choose a new password at next sign-in.${sessionsRevoked > 0 ? ` ${sessionsRevoked} active session${sessionsRevoked === 1 ? ' was' : 's were'} signed out.` : ''}`,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.fieldIssues.length) setErrors(mapFieldIssues<ResetValues>(err.fieldIssues, {}));
      setSubmitError(errorMessage(err, 'Could not reset the password.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Reset Password"
      description={`${user.displayName} · ${user.email}`}
      icon={<KeyRound size={18} aria-hidden="true" />}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="reset-password-form" variant="danger" loading={saving}>
            Reset Password
          </Button>
        </>
      }
    >
      <SubmitError message={submitError} />
      <p className="mb-4 text-[13px] leading-relaxed text-ink-secondary">
        The user is signed out everywhere and must choose a new password at next sign-in. Share the temporary password through a secure channel; it is not
        shown again.
      </p>
      <form id="reset-password-form" noValidate onSubmit={onSubmit} className="grid gap-4" autoComplete="off">
        <FormField label="New Temporary Password" htmlFor={id('newPassword')} required error={errors.newPassword} hint={PASSWORD_HINT}>
          <Input
            id={id('newPassword')}
            type="password"
            autoComplete="new-password"
            value={values.newPassword}
            onChange={(e) => patch({ newPassword: e.target.value })}
            invalid={!!errors.newPassword}
            aria-describedby={fieldDescribedBy(id('newPassword'), { hint: true, error: errors.newPassword })}
            autoFocus
          />
        </FormField>
        <FormField label="Confirm Temporary Password" htmlFor={id('confirm')} required error={errors.confirm}>
          <Input
            id={id('confirm')}
            type="password"
            autoComplete="new-password"
            value={values.confirm}
            onChange={(e) => patch({ confirm: e.target.value })}
            invalid={!!errors.confirm}
            aria-describedby={fieldDescribedBy(id('confirm'), { error: errors.confirm })}
          />
        </FormField>
      </form>
    </Modal>
  );
}
